import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../api";
import { PageHeader, Field, Button, inr } from "../components/UI";
import { Save, Database, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const fields = [
  { key: "paper_cost_per_kg", label: "Paper Cost (₹/kg)", unit: "/kg" },
  { key: "plate_cost_per_set", label: "Plate Cost (₹/set)", unit: "/set" },
  { key: "ink_cost_per_1000", label: "Ink Cost (₹/1000 impressions)", unit: "/1000" },
  { key: "printing_cost_per_1000", label: "Printing Cost (₹/1000 impressions)", unit: "/1000" },
  { key: "lamination_cost_per_sheet", label: "Lamination (₹/sheet)", unit: "/sheet" },
  { key: "uv_cost_per_sheet", label: "UV Coating (₹/sheet)", unit: "/sheet" },
  { key: "foiling_cost_per_sheet", label: "Foiling (₹/sheet)", unit: "/sheet" },
  { key: "packing_cost_flat", label: "Packing Cost (₹ flat per order)", unit: "flat" },
  { key: "delivery_cost_flat", label: "Default Delivery Cost (₹ flat)", unit: "flat" },
  { key: "default_wastage_sheets", label: "Default Wastage (sheets)", unit: "sheets" },
];

export default function RawCosts() {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setValues((await api.get("/raw-costs")).data); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/raw-costs", { values });
      toast.success("Raw costs updated — Pricing Calculator will use new rates");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-[#475569]">Loading…</div>;

  return (
    <div className="p-4 lg:p-8 max-w-4xl" data-testid="raw-costs-page">
      <PageHeader
        title="Raw Cost Master"
        subtitle="Set your standard material & process rates · AI Pricing Engine uses these"
        action={<Button onClick={save} disabled={saving} data-testid="save-raw-btn"><Save size={15}/>{saving ? "Saving…" : "Save Changes"}</Button>}
      />

      <div className="mb-4 p-3 bg-[#FEF2F2] border border-[#FECACA] rounded text-xs text-[#475569] flex items-center gap-2">
        <Database size={14} className="text-[#C1121F]"/>
        Changes apply instantly to all future Pricing Calculator results.
      </div>

      <div className="card-flat p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map((f) => (
            <Field key={f.key} label={f.label}>
              <div className="relative">
                <input
                  data-testid={`raw-${f.key}`}
                  type="number"
                  step="0.01"
                  value={values[f.key] ?? ""}
                  onChange={(e) => setValues({...values, [f.key]: e.target.value})}
                  className="input-base font-mono-num pr-16"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">{f.unit}</span>
              </div>
            </Field>
          ))}
        </div>

        <div className="mt-6 pt-4 border-t border-[#E5E7EB] flex items-center justify-between">
          <button onClick={load} data-testid="reload-raw-btn" className="text-xs text-[#475569] hover:text-[#C1121F] flex items-center gap-1">
            <RefreshCw size={12}/> Reload from server
          </button>
          <div className="text-xs text-[#94A3B8]">
            {values.updated_at && <span>Last updated: <span className="font-mono-num">{values.updated_at.slice(0, 16).replace("T", " ")}</span></span>}
          </div>
        </div>
      </div>
    </div>
  );
}
